import { useState, useEffect } from 'react';
import {
  Radio,
  Network,
  Terminal,
  AlertTriangle,
  Settings,
  BookOpen,
  Gauge,
  RotateCcw,
  MapPin,
  MapPinned,
  BarChart3
} from 'lucide-react';
import { TrafficLight } from './components/regulator/TrafficLight';
import { RegulatorStatus } from './components/regulator/RegulatorStatus';
import { FrameMonitor } from './components/protocol/FrameMonitor';
import { FrameDecoder } from './components/protocol/FrameDecoder';
import { NetworkTopology } from './components/network/NetworkTopology';
import { MapIntersectionView } from './views/MapIntersectionView';
import { SantaColomaMapView } from './views/SantaColomaMapView';
import { GuadalajaraMapView } from './views/GuadalajaraMapView';
import { BarDiagramView } from './views/BarDiagramView';
import { useFrameStore } from './store/frameStore';
import { useDetailedSimulation } from './store/detailedSimulation';
import {
  RegulatorState,
  StateChangeOrigin,
  LightState
} from './types/protocol';
type TabId = 'map' | 'santa-coloma' | 'guadalajara' | 'overview' | 'network' | 'protocol' | 'alarms' | 'diagrama';

// Helper to get light states based on phase
function getLightStates(phase: string): {
  nsRed: LightState;
  nsAmber: LightState;
  nsGreen: LightState;
  ewRed: LightState;
  ewAmber: LightState;
  ewGreen: LightState;
} {
  switch (phase) {
    case 'A': // N-S Green
      return {
        nsRed: LightState.OFF,
        nsAmber: LightState.OFF,
        nsGreen: LightState.ON,
        ewRed: LightState.ON,
        ewAmber: LightState.OFF,
        ewGreen: LightState.OFF,
      };
    case 'B': // N-S Amber (transition)
      return {
        nsRed: LightState.OFF,
        nsAmber: LightState.ON,
        nsGreen: LightState.OFF,
        ewRed: LightState.ON,
        ewAmber: LightState.OFF,
        ewGreen: LightState.OFF,
      };
    case 'C': // E-W Green
      return {
        nsRed: LightState.ON,
        nsAmber: LightState.OFF,
        nsGreen: LightState.OFF,
        ewRed: LightState.OFF,
        ewAmber: LightState.OFF,
        ewGreen: LightState.ON,
      };
    case 'D': // E-W Amber (transition)
      return {
        nsRed: LightState.ON,
        nsAmber: LightState.OFF,
        nsGreen: LightState.OFF,
        ewRed: LightState.OFF,
        ewAmber: LightState.ON,
        ewGreen: LightState.OFF,
      };
    default:
      return {
        nsRed: LightState.OFF,
        nsAmber: LightState.OFF,
        nsGreen: LightState.ON,
        ewRed: LightState.ON,
        ewAmber: LightState.OFF,
        ewGreen: LightState.OFF,
      };
  }
}

const VALID_TABS = new Set<TabId>(['map', 'santa-coloma', 'guadalajara', 'overview', 'network', 'protocol', 'alarms', 'diagrama']);

function getTabFromHash(): TabId {
  const hash = window.location.hash.replace('#', '');
  return VALID_TABS.has(hash as TabId) ? (hash as TabId) : 'diagrama';
}

function App() {
  const [activeTab, setActiveTab] = useState<TabId>(getTabFromHash);

  // Sync hash ↔ tab
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const onHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const { parsedFrame, generateMockFrames, frames } = useFrameStore();

  const {
    isRunning,
    currentPhase,
    cyclePosition,
    cycleTime,
    speed,
    currentPlan,
    start,
    stop,
    reset,
    setSpeed
  } = useDetailedSimulation();

  // Get light states based on current phase
  const lights = getLightStates(currentPhase);

  // Auto-start simulation
  useEffect(() => {
    if (!isRunning) {
      if (frames.length === 0) generateMockFrames();
      start();
    }
  }, []);

  const tabs = [
    { id: 'diagrama' as const, label: 'Diagrama', icon: BarChart3 },
    { id: 'map' as const, label: 'Mapa', icon: MapPin },
    { id: 'santa-coloma' as const, label: 'Santa Coloma', icon: MapPinned },
    { id: 'guadalajara' as const, label: 'Guadalajara', icon: MapPinned },
    { id: 'overview' as const, label: 'Vista General', icon: Radio },
    { id: 'network' as const, label: 'Red', icon: Network },
    { id: 'protocol' as const, label: 'Protocolo', icon: Terminal },
    { id: 'alarms' as const, label: 'Alarmas', icon: AlertTriangle },
  ];

  return (
    <div className="h-screen bg-gray-950 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-ars93.jpg" alt="ARS_93" className="h-9 rounded" />
            <div>
              <h1 className="text-lg font-semibold text-white">ARS_93</h1>
              <p className="text-xs text-gray-500">Gestió de Reguladors</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Simulation status */}
            <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-gray-400">Fase</span>
                <span className="text-sm font-mono font-bold text-green-400">{currentPhase}</span>
              </div>
              <div className="w-px h-4 bg-gray-700" />
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400">Ciclo</span>
                <span className="text-sm font-mono text-blue-400">
                  {(cyclePosition / 10).toFixed(1)}s / {(cycleTime / 10).toFixed(0)}s
                </span>
              </div>
              <div className="w-px h-4 bg-gray-700" />
              <div className="flex items-center gap-1">
                <Gauge className="w-3 h-3 text-gray-500" />
                <span className="text-xs text-gray-400">{speed}x</span>
              </div>
            </div>

            {/* Speed selector */}
            <div className="flex items-center gap-1">
              {[1, 2, 5, 10].map(s => (
                <button
                  key={s}
                  onClick={() => setSpeed(s)}
                  className={`
                    px-2 py-1 text-xs rounded font-medium transition-colors
                    ${speed === s
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'}
                  `}
                >
                  {s}x
                </button>
              ))}
            </div>

            {/* Reset button */}
            <button
              onClick={() => { reset(); start(); }}
              className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
              title="Reiniciar simulación"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            <button className="p-2 text-gray-400 hover:text-white">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-900/50 border-b border-gray-800 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium
                  border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? 'text-white border-blue-500'
                    : 'text-gray-400 border-transparent hover:text-gray-300'}
                `}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        {activeTab === 'map' && (
          <MapIntersectionView />
        )}
        {activeTab === 'santa-coloma' && (
          <SantaColomaMapView />
        )}
        {activeTab === 'guadalajara' && (
          <GuadalajaraMapView />
        )}
        {activeTab === 'diagrama' && (
          <BarDiagramView />
        )}
        <div className={`max-w-7xl mx-auto px-4 py-4 h-full ${activeTab === 'map' || activeTab === 'santa-coloma' || activeTab === 'guadalajara' || activeTab === 'diagrama' ? 'hidden' : ''}`}>
          {activeTab === 'overview' && (
            <div className="grid grid-cols-12 gap-6 h-full">
              {/* Left: Traffic Lights */}
              <div className="col-span-4 space-y-6 overflow-y-auto">
                <div className="bg-gray-900 rounded-lg border border-gray-700 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-medium text-gray-300">
                      Cruce - Fase {currentPhase}
                    </h2>
                    {isRunning && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-xs text-green-400">En simulación</span>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 place-items-center">
                    {/* North-South */}
                    <div></div>
                    <TrafficLight
                      red={lights.nsRed}
                      amber={lights.nsAmber}
                      green={lights.nsGreen}
                      size="lg"
                      label="G1 (N)"
                    />
                    <div></div>

                    {/* East-West */}
                    <TrafficLight
                      red={lights.ewRed}
                      amber={lights.ewAmber}
                      green={lights.ewGreen}
                      size="lg"
                      orientation="horizontal"
                      label="G3 (W)"
                    />
                    <div className="w-16 h-16 bg-gray-800 rounded border border-gray-700 flex items-center justify-center text-xs text-gray-500">
                      CRUCE
                    </div>
                    <TrafficLight
                      red={lights.ewRed}
                      amber={lights.ewAmber}
                      green={lights.ewGreen}
                      size="lg"
                      orientation="horizontal"
                      label="G4 (E)"
                    />

                    <div></div>
                    <TrafficLight
                      red={lights.nsRed}
                      amber={lights.nsAmber}
                      green={lights.nsGreen}
                      size="lg"
                      label="G2 (S)"
                    />
                    <div></div>
                  </div>

                  {/* Phase indicator (read-only when simulating) */}
                  <div className="mt-6 flex gap-2 justify-center">
                    {['A', 'B', 'C', 'D'].map(phase => (
                      <div
                        key={phase}
                        className={`
                          w-10 h-10 rounded-lg font-mono font-bold flex items-center justify-center
                          transition-all duration-300
                          ${currentPhase === phase
                            ? phase === 'B' || phase === 'D'
                              ? 'bg-yellow-600 text-white scale-110'
                              : 'bg-green-600 text-white scale-110'
                            : 'bg-gray-800 text-gray-500'}
                        `}
                      >
                        {phase}
                      </div>
                    ))}
                  </div>

                  {/* Cycle progress bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progreso del ciclo</span>
                      <span>{(cyclePosition / 10).toFixed(1)}s / {(cycleTime / 10).toFixed(0)}s</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-100"
                        style={{ width: `${(cyclePosition / cycleTime) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Regulator Status */}
                <RegulatorStatus
                  state={RegulatorState.REMOTE_CONTROL}
                  stateOrigin={StateChangeOrigin.EXTERNAL}
                  logicalId={1001}
                  physicalId="H0-C1-R1-S0"
                  currentPlan={currentPlan}
                  currentPhase={currentPhase}
                  cycleTime={cycleTime}
                  cyclePosition={cyclePosition}
                  isConnected={isRunning}
                  lastUpdate={new Date()}
                />
              </div>

              {/* Right: Network & Protocol */}
              <div className="col-span-8 space-y-6 overflow-y-auto">
                <NetworkTopology />
                <div className="h-64">
                  <FrameMonitor maxFrames={20} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'network' && (
            <div className="h-full">
              <NetworkTopology className="h-full" />
            </div>
          )}

          {activeTab === 'protocol' && (
            <div className="grid grid-cols-2 gap-4" style={{ height: 'calc(100vh - 180px)' }}>
              {/* Left: Frame Monitor */}
              <div className="flex flex-col min-h-0 overflow-hidden">
                <FrameMonitor maxFrames={100} />
              </div>

              {/* Right: Frame Decoder */}
              <div className="bg-gray-900 rounded-lg border border-gray-700 flex flex-col min-h-0 overflow-hidden">
                <div className="bg-gray-800 px-3 py-2 flex items-center gap-2 flex-shrink-0">
                  <BookOpen className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-gray-300">Decodificador</span>
                  {parsedFrame && (
                    <span className="ml-auto text-xs text-gray-500">
                      {parsedFrame.frameTypeName}
                    </span>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-3 min-h-0">
                  <FrameDecoder parsedFrame={parsedFrame} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'alarms' && (
            <div className="bg-gray-900 rounded-lg border border-gray-700 p-4">
              <h2 className="text-sm font-medium text-gray-300 mb-4">Alarmas Activas</h2>
              <p className="text-gray-500 text-sm">Sin alarmas activas</p>

              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Códigos de alarma del Protocolo B</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    { code: '@', desc: 'Fallo test de equipos' },
                    { code: 'A', desc: 'Alarma de tensión' },
                    { code: 'B', desc: 'Alarma de batería' },
                    { code: 'C', desc: 'Incompatibilidad de color' },
                    { code: 'D', desc: 'Alarma de demanda' },
                    { code: 'E', desc: 'Error del sistema' },
                    { code: 'F', desc: 'Timeout de fase' },
                    { code: 'G', desc: 'Sustitución de grupo' },
                    { code: 'H', desc: 'Tiempo excedido' },
                    { code: 'I', desc: 'Incompatibilidad' },
                    { code: 'J', desc: 'Lámpara fundida' },
                    { code: 'K', desc: 'Manipulación local' },
                    { code: 'L', desc: 'Potencia fundida' },
                    { code: 'M', desc: 'Modo manual' },
                    { code: 'N', desc: 'Alarma de temperatura' },
                    { code: 'R', desc: 'Reset' },
                    { code: 'S', desc: 'Sincronización' },
                    { code: 'T', desc: 'Timeout comunicación' },
                    { code: 'V', desc: 'Rango de tensión' },
                    { code: 'X', desc: 'Alarma externa' }
                  ].map(alarm => (
                    <div key={alarm.code} className="flex items-center gap-2 p-2 bg-gray-800 rounded">
                      <span className="w-6 h-6 flex items-center justify-center bg-red-900/50 text-red-400 rounded font-mono font-bold">
                        {alarm.code}
                      </span>
                      <span className="text-gray-400">{alarm.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 px-4 py-2 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-gray-500">
          <span>ARS_93 — Regulació Semafòrica</span>
          <span>Protocol B · Barcelona</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
